/**
 * Plays the sfxMap entry for every cue `start` (DESIGN-client §1.2, §8). Listens synchronously
 * on the CueQueue's `onStart`; a 40ms per-sample de-dupe keeps instant-mode flushes (reconnect,
 * cascades) from stacking the same hit dozens of times.
 */
import type { Cue, CueData, CueType } from '../scene/cues/cueTypes';
import type { AudioEngine } from './AudioEngine';
import { resolveCueSfx, type ResolvedPlay } from './sfxMap';

/** Structural subset of `net/CueQueue` (`onStart` sync listeners). */
export interface CueStartSource {
  onStart(cb: (cue: Cue, ctx: { instant: boolean }) => void): () => void;
}

export interface SfxDirectorOptions {
  dedupeMs?: number;
  now?: () => number;
  random?: () => number;
  /**
   * Drop a cue before it resolves to any sample. This is how `settings.mutedEmotes` reaches the
   * sound: `SeatsLayer` already refuses to draw a muted sticker, and a muted sticker that still
   * pops is worse than either half alone.
   */
  skip?: (cue: Cue) => boolean;
}

export const DEDUPE_MS = 40;

export class SfxDirector {
  readonly dedupeMs: number;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly lastAt = new Map<string, number>();
  private readonly skip: (cue: Cue) => boolean;
  private unsub: (() => void) | null = null;

  constructor(
    private readonly engine: Pick<AudioEngine, 'play'>,
    opts: SfxDirectorOptions = {},
  ) {
    this.dedupeMs = opts.dedupeMs ?? DEDUPE_MS;
    this.now = opts.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    this.random = opts.random ?? Math.random;
    this.skip = opts.skip ?? (() => false);
  }

  /** Subscribe to a CueQueue-like source; returns the unsubscribe fn (also stored for `detach`). */
  attach(source: CueStartSource): () => void {
    this.detach();
    this.unsub = source.onStart((cue) => {
      if (!this.skip(cue)) this.handle(cue.type, cue.data);
    });
    return () => this.detach();
  }

  detach(): void {
    this.unsub?.();
    this.unsub = null;
  }

  /** Resolve + play; returns what was actually sent to the engine (after de-dupe). */
  handle<T extends CueType>(type: T, data: CueData[T]): ResolvedPlay[] {
    const t = this.now();
    const played: ResolvedPlay[] = [];
    for (const p of resolveCueSfx(type, data, this.random)) {
      const key = p.id;
      const last = this.lastAt.get(key);
      if (last !== undefined && t - last < this.dedupeMs) continue;
      this.lastAt.set(key, t);
      this.engine.play(p.id, { bus: p.bus, gain: p.gain, pitch: p.pitch, delayMs: p.delayMs });
      played.push(p);
    }
    return played;
  }
}
